import { DateTime } from "luxon";
import { classes } from "polytype";
import { omit } from "remeda";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  DataModel,
  extractId,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { Order } from "../../order/index.js";
import { Project } from "../../project/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import type { CertificateType } from "../Certificate/types.js";
import { CertificateContact } from "../CertificateContact/CertificateContact.js";
import type {
  CertificateRequestCertificateData,
  CertificateRequestData,
  CertificateRequestListItemData,
  CertificateRequestListQueryModelData,
} from "./types.js";

export class CertificateRequest extends ReferenceModel {
  public static ofId(certificateRequestId: string) {
    return new CertificateRequest(certificateRequestId);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.certificateRequest.find(id);
    if (data !== undefined) {
      return new CertificateRequestDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const certificateRequest = await this.find(id);
    assertObjectFound(certificateRequest, "certificateRequest", id);
    return certificateRequest;
  });

  public findDetailed = provideReact(
    () => CertificateRequest.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<
    () => Promise<CertificateRequestDetailed | undefined>
  >;

  public getDetailed = provideReact(
    () => CertificateRequest.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CertificateRequestDetailed>>;

  public static query(query: CertificateRequestListQueryModelData = {}) {
    return new CertificateRequestListQuery(query);
  }

  public static async create(
    projectId: string,
    certificate: string,
    privateKey: string,
    certificateAuthority?: string,
  ) {
    const response = await config.behaviors.certificateRequest.create(
      projectId,
      certificate,
      privateKey,
      certificateAuthority,
    );
    if ("id" in response) {
      return new CertificateRequest(response.id);
    }
    return response;
  }

  public async delete() {
    await config.behaviors.certificateRequest.delete(this.id);
  }

  public createOrderPreview = provideReact(
    async (project: Project) =>
      await Order.preview({
        orderType: "externalCertificate",
        orderData: { certificateRequestId: this.id, projectId: project.id },
      }),
    [this.id],
  );

  public async createOrder(project: Project) {
    await Order.create({
      orderType: "externalCertificate",
      orderData: {
        certificateRequestId: this.id,
        projectId: project.id,
      },
    });
  }
}

export class CertificateRequestCommon extends classes(
  DataModel<CertificateRequestData>,
  CertificateRequest,
) {
  public readonly certificateData: CertificateRequestCertificateData;
  public readonly certificateType: CertificateType;
  public readonly commonName?: string;
  public readonly contact?: CertificateContact;
  public readonly createdAt: DateTime;
  public readonly dnsNames: string[];
  public readonly isCompleted: boolean;
  public readonly issuer?: string;
  public readonly project?: Project;
  public readonly validFrom?: DateTime;
  public readonly validTo?: DateTime;
  public constructor(data: CertificateRequestData) {
    super([data], [data.id]);
    this.certificateData = data.certificateData;
    this.certificateType = data.certificateType;
    this.commonName = data.commonName;
    this.contact = data.contact
      ? new CertificateContact(data.contact)
      : undefined;
    this.createdAt = DateTime.fromISO(data.createdAt);
    this.dnsNames = data.dnsNames ?? [];
    this.isCompleted = data.isCompleted;
    this.issuer = data.issuer;
    if (data.projectId) {
      this.project = Project.ofId(data.projectId);
    }
    this.validFrom = data.validFrom
      ? DateTime.fromISO(data.validFrom)
      : undefined;
    this.validTo = data.validTo ? DateTime.fromISO(data.validTo) : undefined;
  }
}

export class CertificateRequestDetailed extends classes(
  CertificateRequestCommon,
  DataModel<CertificateRequestData>,
) {
  public constructor(data: CertificateRequestData) {
    super([data], [data]);
  }
}

export class CertificateRequestListItem extends classes(
  CertificateRequestCommon,
  DataModel<CertificateRequestData>,
) {
  public constructor(data: CertificateRequestListItemData) {
    super([data], [data]);
  }
}

export class CertificateRequestListQuery extends ListQueryModel<CertificateRequestListQueryModelData> {
  public constructor(query: CertificateRequestListQueryModelData = {}) {
    super(query);
  }

  public refine(query: CertificateRequestListQueryModelData = {}) {
    return new CertificateRequestListQuery(query);
  }

  public execute = provideReact(async () => {
    const { items, totalCount } =
      await config.behaviors.certificateRequest.query({
        ...omit(this.query, ["project"]),
        projectId: extractId(this.query.project),
      });
    return new CertificateRequestList(
      this.query,
      items.map((r) => new CertificateRequestListItem(r)),
      totalCount,
    );
  }, [this.queryId, "certificateRequest"]);
}

export class CertificateRequestList extends classes(
  CertificateRequestListQuery,
  ListDataModel<CertificateRequestListItem>,
) {
  public constructor(
    query: CertificateRequestListQueryModelData,
    certificateRequests: CertificateRequestListItem[],
    totalCount: number,
  ) {
    super([query], [certificateRequests, totalCount]);
  }
}
